import BaseTableNew from 'components/atoms/base-table/new';
import {
  MEDICAL_HISTORY_PER_PAGE,
  useGetAllergyLogs,
  useUpdateAllergyLog,
} from 'infrastructure/hooks/ccm';
import {
  type Dispatch,
  type SetStateAction,
  type FC,
  useState,
  useEffect,
} from 'react';
import { t } from 'i18next';
import { isObjectValid } from 'infrastructure/functions';
import AllergyLogForm from 'components/molecules/admin-panel/forms/allergy-log';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import DraggableModal from 'components/organisms/draggableModal';

import { columns } from './columns';
import s from './styles.module.scss';

import type { SortState } from 'infrastructure/types';
import type { Values } from 'components/molecules/admin-panel/forms/allergy-log/validation-schema';
import type { IUpdateAllergyLog } from 'infrastructure/interfaces';

interface AllergyLogTableProps {
  patientGuid: string;
  setPage: Dispatch<SetStateAction<number>>;
  page: number;
  disableActions: boolean;
  refreshToken?: number;
}

const AllergyLogTable: FC<AllergyLogTableProps> = ({
  patientGuid,
  page,
  setPage,
  disableActions,
  refreshToken,
}) => {
  const [open, setOpen] = useState(false);
  const [documentationTimeSort, setDocumentationTimeSort] =
    useState<SortState>('');
  const [updatedAtSort, setUpdatedAtSort] = useState<SortState>('');
  const [epipenRequiredSort, setEpipenRequiredSort] = useState<SortState>('');
  const [logForUpdate, setLogForUpdate] = useState<IUpdateAllergyLog>();
  const { allergyLogs, isLoading, refetch } = useGetAllergyLogs({
    patientGuid,
    documentationTimeSort,
    updatedAtSort,
    epipenRequiredSort,
    meta: { page },
  });
  const { updateAllergyLog, updateLoading } = useUpdateAllergyLog({
    page,
  });
  const toggleOpen = () => setOpen((prev) => !prev);

  const onSubmit = async (values: Values) => {
    if (isObjectValid(values) && logForUpdate && isObjectValid(logForUpdate))
      updateAllergyLog(
        { ...logForUpdate, ...values },
        {
          onSuccess: () => {
            setOpen(false);
            refetch();
          },
        },
      );
  };

  const onCancelForm = () => {
    setLogForUpdate(undefined);
    setOpen(false);
  };

  useEffect(() => {
    if (refreshToken === undefined) return;
    refetch();
  }, [refreshToken, refetch]);

  return (
    <>
      <BaseTableNew
        loading={isLoading}
        items={allergyLogs?.items ?? []}
        columns={columns({
          loading: updateLoading,
          updatedItemGuid: logForUpdate?.guid,
          setLogForUpdate: (v) => {
            toggleOpen();
            setLogForUpdate(v);
          },
          disableActions,
          documentationTimeSort,
          updatedAtSort,
          epipenRequiredSort,
        })}
        bordered
        changeOddRowColor={false}
        rowHoverable={false}
        pagination={{
          current: page,
          pageSize: MEDICAL_HISTORY_PER_PAGE,
          total: allergyLogs?.count ?? 0,
          position: ['bottomRight'],
        }}
        onChangePage={(newPage) => {
          setPage(newPage);
        }}
        dataCy="allergyLog"
        emptyText={
          <EmptyTablePlaceholder text={t('labels.noAllergiesLogged')} />
        }
        onSort={(field, order) => {
          setDocumentationTimeSort('');
          setUpdatedAtSort('');
          setEpipenRequiredSort('');

          if (!order) return;

          if (field === 'updatedAt') {
            setUpdatedAtSort(order);
          } else if (field === 'documentationTime') {
            setDocumentationTimeSort(order);
          } else if (field === 'epipenRequired') {
            setEpipenRequiredSort(order);
          }
        }}
      />
      {open && (
        <DraggableModal
          title={t('labels.editAllergy')}
          isOpen={open}
          toggle={toggleOpen}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
        >
          <AllergyLogForm
            loading={updateLoading}
            onSubmit={onSubmit}
            onCancelForm={onCancelForm}
            defaultValues={logForUpdate}
          />
        </DraggableModal>
      )}
    </>
  );
};
export default AllergyLogTable;
